import { dao } from '@platform/dao';
import type { IRecordTypeOverrideDoc } from '@platform/dao';
import type { IRecordTypeDefinition } from '@platform/core/types';
import type { IGlobalOverrideArg } from './args/IGlobalOverrideArg';
import type { ISaveGlobalOverrideArg } from './args/ISaveGlobalOverrideArg';
import { baseApi, run } from './baseApi';
import { refreshModuleCaches } from './refreshModuleCaches';

const overrideTag = (rt: string) => ({ type: 'GlobalOverride' as const, id: rt });

/**
 * Глобальні перевизначення типів записів (ADR-027 «Глобальні визначення»; «Налаштування › Глобально»: форма й назва типу для всіх
 * робочих просторів без власного налаштування). Збереження кладе патч і нову версію в кеш (наступна зміна — з правильним If-Match), злитий
 * тип — у глобальні визначення, а визначення модуля в кожному закешованому просторі перечитуються (refreshModuleCaches). Скидання (DELETE)
 * перечитує все.
 */
export const globalOverrideApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    globalRecordTypeOverride: b.query<IRecordTypeOverrideDoc, IGlobalOverrideArg>({
      queryFn: ({ rt }) => run(() => dao.globals.override(rt)),
      serializeQueryArgs: ({ queryArgs }) => queryArgs.rt,
      providesTags: (_r, _e, { rt }) => [overrideTag(rt)],
    }),
    saveGlobalRecordTypeOverride: b.mutation<IRecordTypeDefinition, ISaveGlobalOverrideArg>({
      queryFn: ({ rt, patch, version }) => run(() => dao.globals.saveOverride(rt, patch, version)),
      async onQueryStarted({ rt, module, patch, version }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled.catch(() => ({ data: undefined }));
        if (!data) return;
        dispatch(globalOverrideApi.util.upsertQueryData('globalRecordTypeOverride', { rt }, { patch, version: version + 1 }));
        await dispatch(refreshModuleCaches(module, data));
      },
    }),
    resetGlobalRecordTypeOverride: b.mutation<void, IGlobalOverrideArg & { module: string }>({
      queryFn: ({ rt }) => run(() => dao.globals.resetOverride(rt)),
      async onQueryStarted({ rt, module }, { dispatch, queryFulfilled }) {
        const ok = await queryFulfilled.then(() => true, () => false);
        if (!ok) return;
        dispatch(globalOverrideApi.util.upsertQueryData('globalRecordTypeOverride', { rt }, { patch: {}, version: 0 }));
        await dispatch(refreshModuleCaches(module));
      },
    }),
  }),
});

export const { useGlobalRecordTypeOverrideQuery, useSaveGlobalRecordTypeOverrideMutation, useResetGlobalRecordTypeOverrideMutation } = globalOverrideApi;
